/* ========================================
   SYNC THEME - GLOBAL.JS
   Base utilities and helpers
   ======================================== */

// Debounce utility
function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

// Throttle utility
function throttle(fn, wait) {
  let inThrottle, lastFn, lastTime;
  return function() {
    const context = this, args = arguments;
    if (!inThrottle) {
      fn.apply(context, args);
      lastTime = Date.now();
      inThrottle = true;
    } else {
      clearTimeout(lastFn);
      lastFn = setTimeout(() => {
        if (Date.now() - lastTime >= wait) {
          fn.apply(context, args);
          lastTime = Date.now();
        }
      }, Math.max(wait - (Date.now() - lastTime), 0));
    }
  };
}

// Fetch with error handling
async function fetchJSON(url, options = {}) {
  try {
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Fetch error:', error);
    throw error;
  }
}

// Local storage helpers
const storage = {
  get(key, defaultValue = null) {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.error('Storage get error:', error);
      return defaultValue;
    }
  },
  
  set(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      console.error('Storage set error:', error);
      return false;
    }
  },
  
  remove(key) {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error('Storage remove error:', error);
      return false;
    }
  }
};

// DOM ready helper
function onReady(fn) {
  if (document.readyState !== 'loading') {
    fn();
  } else {
    document.addEventListener('DOMContentLoaded', fn);
  }
}

// Trap focus within element
function trapFocus(element) {
  const focusableElements = element.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );
  
  if (focusableElements.length === 0) return;
  
  const firstFocusable = focusableElements[0];
  const lastFocusable = focusableElements[focusableElements.length - 1];
  
  element.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;
    
    if (e.shiftKey) {
      if (document.activeElement === firstFocusable) {
        lastFocusable.focus();
        e.preventDefault();
      }
    } else {
      if (document.activeElement === lastFocusable) {
        firstFocusable.focus();
        e.preventDefault();
      }
    }
  });
  
  firstFocusable.focus();
}

// Remove focus trap
function removeTrapFocus(element) {
  if (!element) return;
  
  const newElement = element.cloneNode(true);
  element.parentNode.replaceChild(newElement, element);
}

// Format money
function formatMoney(cents, format = '${{amount}}') {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }
  
  let value = '';
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format || '${{amount}}';
  
  function defaultOption(opt, def) {
    return typeof opt === 'undefined' ? def : opt;
  }
  
  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ',');
    decimal = defaultOption(decimal, '.');
    
    if (isNaN(number) || number == null) {
      return '0';
    }
    
    number = (number / 100.0).toFixed(precision);
    
    const parts = number.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const cents = parts[1] ? decimal + parts[1] : '';
    
    return dollars + cents;
  }
  
  switch (formatString.match(placeholderRegex)[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
  }
  
  return formatString.replace(placeholderRegex, value);
}

// Convenience wrapper: format cents using the store's own money format
// (set in theme.liquid from shop.money_format). Falls back to USD.
function formatMoneyShop(cents) {
  var fmt = (window.SYNC && window.SYNC.moneyFormat) ? window.SYNC.moneyFormat : '${{amount}}';
  return formatMoney(cents, fmt);
}

// Export utilities — MERGE so values set inline in theme.liquid
// (moneyFormat, routes) are preserved, not overwritten.
window.SYNC = Object.assign(window.SYNC || {}, {
  debounce,
  throttle,
  fetchJSON,
  storage,
  onReady,
  trapFocus,
  removeTrapFocus,
  formatMoney,
  formatMoneyShop
});

// Scroll-driven marquee: animate only while scrolling (matches Lovable Marquee.tsx)
function initScrollMarquee() {
  var marquees = document.querySelectorAll('.marquee, .cmarquee');
  if (!marquees.length) return;
  var timer = null;
  var lastY = window.scrollY;

  function bump() {
    marquees.forEach(function (m) { m.classList.add('is-scrolling'); });
    if (timer) clearTimeout(timer);
    timer = setTimeout(function () {
      marquees.forEach(function (m) { m.classList.remove('is-scrolling'); });
    }, 200);
  }

  window.addEventListener('scroll', function () {
    lastY = window.scrollY;
    bump();
  }, { passive: true });

  // Catch in-progress scroll after load (poll a short window)
  var frames = 0;
  function tick() {
    if (window.scrollY !== lastY) { lastY = window.scrollY; bump(); }
    if (frames++ < 60) requestAnimationFrame(tick);
  }
  requestAnimationFrame(tick);
}

// Sync wishlist count badge in header from localStorage (matches Lovable wishCount)
function initWishlistCount() {
  var WISHLIST_KEY = 'sync:wishlist';
  function getCount() {
    try { return (JSON.parse(localStorage.getItem(WISHLIST_KEY)) || []).length; }
    catch (e) { return 0; }
  }
  function update(count) {
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }
  update(getCount());
  document.addEventListener('wishlist:change', function (e) {
    var count = (e.detail && typeof e.detail.count === 'number') ? e.detail.count : getCount();
    update(count);
  });
}

// Initialize
onReady(() => {
  initScrollMarquee();
  initWishlistCount();
  console.log('SYNC theme loaded');
});
